import {
  toWSDateString,
  WSAvatarIconProps,
  WSCard,
  WSElement,
  WSEmptyState,
  WSGrid,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayrollWorkflowStrategy } from "@wingspanhq/payments/dist/interfaces";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import {
  ListFormsSortParameterValue,
  PayableRow,
  PayableStatus,
  PayeeReviewStatus
} from "@wingspanhq/wingspan-api-sdk";
import { queriesPayableRows } from "@query";
import React, { ReactNode, useCallback, useMemo } from "react";
import { Route, useHistory } from "react-router-dom";
import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { Summary } from "../../../Invoices/components/Summary/Summary";
import { PayableDetails } from "../../../Invoices/screens/payables/PayableDetails";
import { getNextPayrollCutOffDate } from "../../../Invoices/utils/payables";
import { useUserId } from "../../../query/hooks/helpers";
import { useQueryPayrollSettings } from "../../../query/payments/queries";
import { useDefaultCurrency } from "../../../shared/hooks/useDefaultCurrency";
import { PATH_SETTINGS_PAYROLL_SCHEDULE } from "../../Settings/paths";
import { getTabFilter } from "../components/TablePayables/filters/request";
import { TablePayables } from "../components/TablePayables/TablePayables";
import {
  PATH_PAYABLES_DISPUTED,
  routeHelpersPayablesDetails,
  routeHelpersPayablesTab
} from "../paths";

const RECENT_ACTIVITY_LIMIT = 5;
const COUNT_PAGE = { size: 1 };

type InfoCardProps = {
  avatar: Pick<WSAvatarIconProps, "icon" | "color" | "colorBackground">;
  headerText: string;
  actionLabel: string;
  onAction: () => void;
  children: ReactNode;
};

const InfoCard: React.FC<InfoCardProps> = props => (
  <WSCard
    header={{
      label: {
        avatar: { type: "icon", size: "M", ...props.avatar },
        text: props.headerText
      }
    }}
    divider="bottom"
    actions={{
      alignment: "left",
      buttons: [
        {
          label: props.actionLabel,
          kind: "Link",
          rightIcon: "chevron-right",
          onClick: props.onAction
        }
      ]
    }}
  >
    <WSText.ParagraphSm color="gray500">{props.children}</WSText.ParagraphSm>
  </WSCard>
);

const pluralPayable = (count: number) => `payable${count > 1 ? "s" : ""}`;

export const RoutePayablesDashboard: React.FC = () => {
  useBrowserPageTitle("Payables Dashboard");
  const userId = useUserId();
  const history = useHistory();
  const defaultCurrency = useDefaultCurrency();

  // Both boundaries are pinned for the screen's lifetime: a `Date` built during
  // render lands in the search request, and since it is part of the query cache
  // key a fresh one each render invalidates the key and refetches in a loop.
  const { paidSince, overdueBefore } = useMemo(() => {
    const overdueBefore = new Date();
    const paidSince = new Date(overdueBefore);
    paidSince.setHours(0, 0, 0, 0);
    paidSince.setDate(paidSince.getDate() - 30);
    return { paidSince, overdueBefore };
  }, []);

  const queryPayrollSettings = useQueryPayrollSettings(userId, {
    retry: false
  });
  const isDualStage =
    queryPayrollSettings.data?.workflow === PayrollWorkflowStrategy.DualStage;

  const queryOpenSummary = queriesPayableRows.searchPayableRows.useQuery({
    page: COUNT_PAGE,
    filter: {
      status: { anyOf: [PayableStatus.OPENED, PayableStatus.PENDING] }
    }
  });
  // v3 has no `Overdue` status; overdue is derived from an opened payable that
  // is past its due date.
  const queryOverdueSummary = queriesPayableRows.searchPayableRows.useQuery({
    page: COUNT_PAGE,
    filter: {
      status: { anyOf: [PayableStatus.OPENED] },
      dueDate: { lt: overdueBefore }
    }
  });
  const queryPaidLast30DaysSummary =
    queriesPayableRows.searchPayableRows.useQuery({
      page: COUNT_PAGE,
      filter: {
        ...getTabFilter("paid").filter,
        events_paidAt: { gte: paidSince }
      }
    });
  const queryScheduledSummary = queriesPayableRows.searchPayableRows.useQuery({
    page: COUNT_PAGE,
    filter: getTabFilter("scheduled").filter
  });
  const queryToApproveSummary = queriesPayableRows.searchPayableRows.useQuery({
    page: COUNT_PAGE,
    filter: getTabFilter("to-approve", isDualStage).filter
  });
  const queryDisputedSummary = queriesPayableRows.searchPayableRows.useQuery({
    page: COUNT_PAGE,
    filter: { payeeReviewStatus: { eq: PayeeReviewStatus.DISPUTED } }
  });

  const queryRecentPayables = queriesPayableRows.searchPayableRows.useQuery({
    page: { size: RECENT_ACTIVITY_LIMIT },
    sort: { updatedAt: ListFormsSortParameterValue.DESC }
  });

  const formatTotal = useCallback(
    (totalValue: number | undefined) =>
      totalValue !== undefined ? formatMoney(totalValue, defaultCurrency) : "–",
    [defaultCurrency]
  );

  const goToPayableDetails = routeHelpersPayablesDetails.useGoTo();
  const goToPayablesTab = routeHelpersPayablesTab.useGoTo();

  const handleRowClick = useCallback(
    (payable: PayableRow) => {
      if (payable.id) {
        goToPayableDetails({
          tab: "dashboard",
          payableId: payable.id
        });
      }
    },
    [goToPayableDetails]
  );

  const goToToApprove = () => goToPayablesTab({ tab: "to-approve" });
  const goToDisputed = () => history.push(PATH_PAYABLES_DISPUTED);

  const scheduledSummary = queryScheduledSummary.data?.summary;
  const toApproveSummary = queryToApproveSummary.data?.summary;
  const disputedSummary = queryDisputedSummary.data?.summary;
  const recentPayables = queryRecentPayables.data?.data ?? [];

  return (
    <>
      <Route
        path={routeHelpersPayablesDetails.path}
        component={PayableDetails}
      />
      <WSList gap="2XL">
        <WSElement>
          <WSGrid>
            <WSGrid.Item span={{ xs: "6", s: "4", m: "3" }}>
              <Summary
                label="Open"
                color="amber300"
                value={formatTotal(queryOpenSummary.data?.summary?.amountTotal)}
              />
            </WSGrid.Item>
            <WSGrid.Item span={{ xs: "6", s: "4", m: "3" }}>
              <Summary
                label="Overdue"
                color="red300"
                value={formatTotal(
                  queryOverdueSummary.data?.summary?.amountTotal
                )}
              />
            </WSGrid.Item>
            <WSGrid.Item span={{ xs: "8", s: "4", m: "3" }}>
              <Summary
                label="Paid (last 30 days)"
                color="green300"
                value={formatTotal(
                  queryPaidLast30DaysSummary.data?.summary?.amountTotal
                )}
              />
            </WSGrid.Item>
          </WSGrid>
        </WSElement>

        <WSList gap="L">
          {!!scheduledSummary?.count && !!queryPayrollSettings.data && (
            <InfoCard
              avatar={{
                icon: "calendar",
                color: "blue500",
                colorBackground: "blue50"
              }}
              headerText={`Next payroll: ${toWSDateString(getNextPayrollCutOffDate(queryPayrollSettings.data), "monthDate")}`}
              actionLabel="Manage payroll settings"
              onAction={() => {
                history.push(PATH_SETTINGS_PAYROLL_SCHEDULE);
              }}
            >
              {scheduledSummary.count} {pluralPayable(scheduledSummary.count)}{" "}
              totaling{" "}
              {formatMoney(scheduledSummary.amountTotal, defaultCurrency)}
            </InfoCard>
          )}

          {!!toApproveSummary?.count && (
            <InfoCard
              avatar={{
                icon: "alert",
                color: "amber400",
                colorBackground: "amber50"
              }}
              headerText="To approve:"
              actionLabel="Review and approve"
              onAction={goToToApprove}
            >
              {toApproveSummary.count} {pluralPayable(toApproveSummary.count)}{" "}
              totaling{" "}
              {formatMoney(toApproveSummary.amountTotal, defaultCurrency)}
            </InfoCard>
          )}

          {!!disputedSummary?.count && (
            <InfoCard
              avatar={{
                icon: "alert-circle",
                color: "amber400",
                colorBackground: "amber50"
              }}
              headerText="Disputed payables"
              actionLabel="Manage disputed payables"
              onAction={goToDisputed}
            >
              {disputedSummary.count} disputed{" "}
              {pluralPayable(disputedSummary.count)} that need your attention.
            </InfoCard>
          )}
        </WSList>

        <WSElement>
          <WSText.Heading5 mb="M">Recent payable activity</WSText.Heading5>
          {recentPayables.length === 0 ? (
            <WSEmptyState
              py="3XL"
              hasBorder
              bgColor="gray"
              type="inbox"
              title="No recent activity"
              description="New and recently changed payables will display here."
            />
          ) : (
            <TablePayables
              payables={recentPayables}
              loading={queryRecentPayables.isLoading}
              onRowClick={handleRowClick}
              visibleColumns={{
                payee: true,
                payableNumber: true,
                amount: true,
                dueDate: false,
                status: true
              }}
            />
          )}
        </WSElement>
      </WSList>
    </>
  );
};
